import { GizmoData, Scene, loadScene } from '../../SceneLoader';
import { MathUtils, Vector3 } from 'three';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';

import CameraController from '../threejs/CameraController';
import Canvas3 from '../threejs/Canvas3';
import { Content } from '../../Content';
import ContentPopup from './content_popup/ContentPopup';
import Gizmo from '../threejs/Gizmo';
import { Helmet } from 'react-helmet';
import PanoSphere from '../threejs/PanoSphere';
import { SceneContext } from '../../contexts/SceneContext';
import { SceneInfo } from './SceneInfo/SceneInfo';
import { ScreenMessage } from './ScreenMessage/ScreenMessage';
import { satisfies } from 'semver';
import { version } from '../../../package.json';

type State = {
  content: Content | null;
};

export type Action = {
  type: 'content-set';
  payload: { content: Content | null };
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'content-set':
      return { ...state, content: action.payload.content };
  }
};

export interface Props {
  sceneID: string;
}

export const SceneViewer: React.FC<Props> = (props) => {
  const [scene, setScene] = useState<Scene | null>(null);

  const [isBGLoaded, setIsBGLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [{ content: activeContent }, dispatch] = useReducer(reducer, {
    content: null,
  });

  const openGizmo = useCallback((gizmo: GizmoData) => {
    dispatch({
      type: 'content-set',
      payload: { content: gizmo.content },
    });
  }, []);

  useEffect(() => {
    loadScene(props.sceneID)
      .then((scene) => {
        // Check version compatability
        if (satisfies(version, scene.data.builtFor) === false) {
          console.error(
            `Unable to open scene '${scene.id}': Version incompatibility {appVersion: ${version}, builtFor: ${scene.data.builtFor}}`
          );
          setError('The requested scene cannot be opened');
          setScene(null);
          return;
        }

        setScene(scene);
      })
      .catch((err) => {
        console.error(err);
        setError('An issue occured while loading the scene');
        setScene(null);
      });
  }, [props.sceneID]);

  const handleBGTextureLoaded = useCallback(() => {
    setIsBGLoaded(true);
  }, [setIsBGLoaded]);

  const handleBGTextureUnloaded = useCallback(() => {
    setIsBGLoaded(false);
  }, [setIsBGLoaded]);

  const parseContentPath = useCallback(
    (path: string) => {
      if (scene !== null)
        path = path.replaceAll('{project_path}', `projects/${scene.id}`);
      return path;
    },
    [scene]
  );

  return (
    <SceneContext.Provider
      value={{
        scene,
        projectPath: scene ? `projects/${scene.id}` : null,
        parseContentPath: parseContentPath,
      }}
    >
      {/* Head Information */}
      <Helmet>
        <meta name="app-version" content={version} />
        {scene && <title>{scene.data.title}</title>}
        {scene && <meta name="scene-version" content={scene.data.version} />}
      </Helmet>

      <div
        style={{
          width: '100vw',
          height: '100vh',
          position: 'absolute',
          top: '0',
        }}
      >
        {/* ThreeJS Canvas */}
        <Canvas3
          onCreated={({ camera }) => {
            if (scene?.data.initialRotation == null) return;
            const { 0: x, 1: y, 2: z } = scene.data.initialRotation;
            camera.rotation.x = MathUtils.degToRad(x);
            camera.rotation.y = MathUtils.degToRad(y);
            camera.rotation.z = MathUtils.degToRad(z);
            camera.updateProjectionMatrix();
          }}
        >
          <CameraController />

          {/* Pano Sphere */}
          {scene && (
            <Suspense fallback={<group />}>
              <PanoSphere
                position={[0, 0, 0]}
                texturePath={parseContentPath(scene.data.bg)}
                onTextureLoaded={handleBGTextureLoaded}
                onTextureUnloaded={handleBGTextureUnloaded}
              />
            </Suspense>
          )}

          {/* Gizmos */}
          {scene &&
            isBGLoaded &&
            scene.data.gizmos.map((value) => (
              <Gizmo
                key={value.id}
                gizmoData={value}
                onClick={() => {
                  openGizmo(value);
                }}
                dispatch={dispatch}
                position={new Vector3(...value.direction).setLength(9)}
              />
            ))}
        </Canvas3>

        {/* Scene Info */}
        {scene && isBGLoaded && (
          <SceneInfo
            notice={
              scene.data.isDraft
                ? {
                    header: 'DRAFT',
                    body: 'For internal discussion only',
                  }
                : undefined
            }
            info={
              process.env.NODE_ENV === 'development'
                ? [`App ${version} | Scene ${scene.data.version}`]
                : undefined
            }
          />
        )}

        {/* Content Popup */}
        {activeContent && (
          <ContentPopup
            content={activeContent}
            onCloseRequested={() =>
              dispatch({ type: 'content-set', payload: { content: null } })
            }
          />
        )}

        {/* Loading / Errors */}
        {!isBGLoaded && (
          <ScreenMessage
            message={error !== null ? error : 'Loading, please wait...'}
          />
        )}
      </div>
    </SceneContext.Provider>
  );
};
